import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FiMapPin, FiPhone, FiUser, FiFileText, FiPlus, FiEdit3, FiCreditCard, FiTruck } from "react-icons/fi";
import Nav from "../../components/home/Nav";
import { syncCart } from "../../store/reducers/cartReducer";
import { useApplyDiscountCodeMutation, useCreateOrderMutation, useShippingMethodsQuery, useUserAddressesQuery, useValidateCartMutation } from "../../store/services/checkoutService";
import { useCheckSnappEligibleQuery, usePaymentSettingsQuery, useRequestPaymentMutation, useRequestSnappPaymentMutation } from "../../store/services/paymentService";
import { getFinalPrice } from "../../utils/productPrice";
import { imageUrl } from "../../utils/imageUrl";
import { toPersianNumber } from "../../utils/persian";
import { useFeedback } from "../../context/AppFeedbackContext";
import FieldError from "../../components/ui/FieldError";
import SupportHint from "../../components/ui/SupportHint";
import Money from "../../components/ui/Money";
import { getApiErrors, getFieldError, getFriendlyApiError } from "../../utils/formErrors";
import "../../styles/form-errors.css";
import { orderSuccessUrl, orderRef } from "../../utils/orderUrl";
import { clearPendingPayment, pendingPaymentOrderRef, rememberPendingPayment } from "../../utils/pendingPayment";
import { trackPaymentTelemetry, goToTrackedPaymentUrl } from "../../utils/paymentTelemetry";
import { trackAnalytics } from "../../utils/analytics";
import { canAutoRedirectPayment, copyPaymentUrl, getPaymentBrowserInfo, getPaymentRedirectCopy, getPaymentUrl, isEmbeddedPaymentBrowser } from "../../utils/paymentRedirect";
import "../../styles/payment-redirect.css";

const splitFullName = (value = "") => {
  const parts = String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length <= 1) {
    return {
      firstName: parts[0] || "",
      lastName: ""
    };
  }

  const lastName = parts.pop();

  return {
    firstName: parts.join(" "),
    lastName
  };
};

const emptyForm = {
  customer_first_name: "",
  customer_last_name: "",
  customer_mobile: "",
  province: "",
  city: "",
  address: "",
  postal_code: "",
  plaque: "",
  unit: "",
  description: "",
  note: "",
  save_address: true
};

const snappPayLogo = "/images/payment/snapp-pay/40x40@3x.png";

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useFeedback();

  const { cart, total } = useSelector((state) => state.cartReducer);
  const { userToken, user } = useSelector((state) => state.authReducer);

  const [createOrder, response] = useCreateOrderMutation();
  const [applyDiscountCode, discountResponse] = useApplyDiscountCodeMutation();
  const [requestPayment, paymentResponse] = useRequestPaymentMutation();
  const [requestSnappPayment, snappPaymentResponse] = useRequestSnappPaymentMutation();
  const [validateCart, validateCartResponse] = useValidateCartMutation();

  const { data: addressesData, isFetching: addressesLoading } = useUserAddressesQuery(undefined, {
    skip: !userToken
  });

  const { data: shippingData, isFetching: shippingLoading } = useShippingMethodsQuery(total, {
    skip: !userToken || !cart.length
  });

  const paymentSettingsResponse = usePaymentSettingsQuery();
  const snappPayEnabled = Boolean(paymentSettingsResponse.data?.snapp_pay?.enabled);

  const addresses = useMemo(() => addressesData?.addresses || [], [addressesData]);
  const shippingMethods = useMemo(() => shippingData?.shipping_methods || [], [shippingData]);

  const defaultForm = useMemo(() => {
    const fullName =
      user?.name === "کاربر جوتی"
        ? ""
        : user?.name || "";

    const {
      firstName,
      lastName
    } = splitFullName(fullName);

    return {
      ...emptyForm,
      customer_first_name: firstName,
      customer_last_name: lastName,
      customer_mobile: user?.mobile || ""
    };
  }, [user]);

  const [mode, setMode] = useState("new");
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [paymentRedirect, setPaymentRedirect] = useState(null);
  const [copyPaymentMessage, setCopyPaymentMessage] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("online_gateway");
  const checkoutAnalyticsTrackedRef = useRef(false);

  useEffect(() => {
    setForm((prev) => ({
      ...defaultForm,
      note: prev.note || ""
    }));
  }, [defaultForm]);

  useEffect(() => {
    if (addresses.length > 0 && !selectedAddressId) {
      const defaultAddress = addresses.find((item) => item.is_default) || addresses[0];
      setSelectedAddressId(String(defaultAddress.id));
      setMode("saved");
    }
  }, [addresses, selectedAddressId]);

  useEffect(() => {
    if (shippingMethods.length > 0 && !selectedShippingMethodId) {
      const preferredMethod =
        shippingMethods.find((method) => method.type === "fixed") ||
        shippingMethods.find((method) => method.type === "pay_later") ||
        shippingMethods[0];

      setSelectedShippingMethodId(String(preferredMethod.id));
    }
  }, [shippingMethods, selectedShippingMethodId]);

  const selectedAddress = addresses.find((item) => String(item.id) === String(selectedAddressId));
  const selectedShippingMethod = shippingMethods.find((item) => String(item.id) === String(selectedShippingMethodId));
  const shippingCost = selectedShippingMethod?.type === "fixed" ? Number(selectedShippingMethod.cost || 0) : 0;
  const discountAmount = Number(appliedDiscount?.discount_amount || 0);
  const shippingDiscountAmount = Number(appliedDiscount?.shipping_discount_amount || 0);
  const payableTotal = Math.max(total + shippingCost - discountAmount - shippingDiscountAmount, 0);
  const snappEligibilityResponse = useCheckSnappEligibleQuery(payableTotal, {
    skip: !snappPayEnabled || payableTotal <= 0,
    refetchOnMountOrArgChange: true
  });
  const snappPayEligible = Boolean(snappEligibilityResponse.data?.eligible);
  const snappPayChecking = snappEligibilityResponse.isLoading || snappEligibilityResponse.isFetching;
  const snappEligibleRaw = snappEligibilityResponse.data || {};
  const snappEligibleNested = snappEligibleRaw.response?.response || snappEligibleRaw.response || {};
  const snappPayTitle = snappEligibleRaw.title_message || snappEligibleNested.title_message || "پرداخت قسطی و اعتباری با اسنپ‌پی";
  const snappPayDescription = snappPayChecking
    ? "در حال بررسی امکان خرید اعتباری..."
    : snappEligibleRaw.description || snappEligibleNested.description || "۴ قسط بدون کارمزد";
  const snappMinimumAmount = Number(snappEligibleRaw.minimum_amount_toman || 4000);
  const snappPayAmountEligible = payableTotal >= snappMinimumAmount;
  const shouldShowSnappPay =
    snappPayEnabled &&
    snappPayAmountEligible &&
    snappEligibilityResponse.isSuccess &&
    snappPayEligible;
  const snappPaySelectable = snappPayEligible && !snappPayChecking;
  const isRedirectingPayment = Boolean(paymentRedirect);
  const shouldShowPaymentBrowserHint = isEmbeddedPaymentBrowser();

  useEffect(() => {
    const snappUnavailable =
      !snappPayEnabled ||
      !snappPayAmountEligible ||
      (snappEligibilityResponse.isSuccess && !snappPayEligible);

    if (snappUnavailable && selectedPaymentMethod === "snapp_pay") {
      setSelectedPaymentMethod("online_gateway");
    }
  }, [
    selectedPaymentMethod,
    snappEligibilityResponse.isSuccess,
    snappPayAmountEligible,
    snappPayEligible,
    snappPayEnabled
  ]);

  useEffect(() => {
    setAppliedDiscount(null);
    setDiscountError("");
  }, [total, selectedShippingMethodId, cart]);

  useEffect(() => {
    if (checkoutAnalyticsTrackedRef.current || !cart.length) return;

    checkoutAnalyticsTrackedRef.current = true;

    trackAnalytics("checkout_start", {
      metadata: {
        source: "checkout_page",
        items_count: cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        cart_total: Number(total || 0)
      }
    });
  }, [cart, total]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const fillFromAddress = (address) => {
    const {
      firstName,
      lastName
    } = splitFullName(address.receiver_name || "");

    setForm({
      customer_first_name: firstName,
      customer_last_name: lastName,
      customer_mobile: address.receiver_mobile || "",
      province: address.province || "",
      city: address.city || "",
      address: address.address || "",
      postal_code: address.postal_code || "",
      plaque: address.plaque || "",
      unit: address.unit || "",
      description: address.description || "",
      note: form.note || "",
      save_address: true
    });
    setMode("new");
    setSelectedAddressId("");
  };

  const getErrorMessage = (err) => getFriendlyApiError(err, "اطلاعات سفارش را بررسی کنید.");

  const startPaymentRedirect = (paymentUrl, method = selectedPaymentMethod, context = {}) => {
    const autoRedirect = canAutoRedirectPayment();
    const copy = getPaymentRedirectCopy(method);
    const browser = getPaymentBrowserInfo();
    const meta = {
      ...context,
      payment_method: method,
      auto_redirect: autoRedirect,
      browser
    };

    setCopyPaymentMessage("");
    setPaymentRedirect({
      ...copy,
      url: paymentUrl,
      autoRedirect,
      context: meta
    });

    trackPaymentTelemetry("payment_redirect_screen_shown", {
      metadata: meta
    });

    if (autoRedirect) {
      window.setTimeout(() => {
        goToTrackedPaymentUrl(paymentUrl, {
          ...meta,
          trigger: "auto"
        });
      }, browser.embedded ? 550 : 350);
    }
  };

  const copyCurrentPaymentUrl = async () => {
    if (!paymentRedirect?.url) return;

    trackPaymentTelemetry("payment_copy_link_clicked", {
      metadata: {
        ...(paymentRedirect?.context || {}),
        trigger: "checkout_overlay"
      }
    });

    const copied = await copyPaymentUrl(paymentRedirect.url);
    setCopyPaymentMessage(copied ? "لینک پرداخت کپی شد." : "کپی لینک انجام نشد.");
  };

  const notifyCartSync = (res) => {
    const summary = res?.summary || {};
    const removedCount = Number(summary.removed_count || 0);
    const quantityChangedCount = Number(summary.quantity_changed_count || 0);

    if (removedCount > 0) {
      notify("کالای ناموجود حذف شد.", "warning", "سبد خرید");
      return;
    }

    if (quantityChangedCount > 0) {
      notify("تعداد کالاها به‌روز شد.", "warning", "سبد خرید");
    }
  };

  const checkCartBeforeSubmit = async () => {
    const requestedRecoveryOrderRef = pendingPaymentOrderRef(cart);

    const res = await validateCart({
      cart,
      ...(requestedRecoveryOrderRef
        ? { recovery_order_ref: requestedRecoveryOrderRef }
        : {})
    }).unwrap();

    const recoveryOrderRef = String(res?.recovery_order_ref || "").trim() || null;

    if (requestedRecoveryOrderRef && !recoveryOrderRef) {
      clearPendingPayment(requestedRecoveryOrderRef);
    }

    const nextCart = Array.isArray(res.cart) ? res.cart : cart;

    if (Array.isArray(res.cart)) {
      dispatch(syncCart(res.cart));
    }

    notifyCartSync(res);

    if (!nextCart.length) {
      setError("سبد خرید قابل ثبت نیست.");
      return null;
    }

    return {
      cart: nextCart,
      recoveryOrderRef
    };
  };

  const applyCode = async () => {
    setDiscountError("");
    setAppliedDiscount(null);
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.code;
      delete next.discount_code;
      return next;
    });

    if (!discountCode.trim()) {
      setDiscountError("کد تخفیف را وارد کنید.");
      setFormErrors((prev) => ({ ...prev, code: ["کد تخفیف را وارد کنید."] }));
      return;
    }

    if (!selectedShippingMethod) {
      setDiscountError("ابتدا روش ارسال را انتخاب کنید.");
      return;
    }

    try {
      const res = await applyDiscountCode({
        code: discountCode.trim(),
        shipping_method_id: selectedShippingMethod.id,
        cart
      }).unwrap();

      setAppliedDiscount(res.discount);
    } catch (err) {
      const errors = getApiErrors(err);
      setFormErrors((prev) => ({ ...prev, ...errors }));
      setDiscountError(getErrorMessage(err));
    }
  };


  const submitOrder = async (e) => {
    e.preventDefault();
    setError("");
    setFormErrors({});

    if (!userToken) {
      sessionStorage.setItem("redirectAfterLogin", "/checkout");
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }

    if (!cart.length) {
      navigate("/cart");
      return;
    }

    if (!selectedShippingMethod) {
      setError("روش ارسال را انتخاب کنید.");
      setFormErrors({ shipping_method_id: ["روش ارسال را انتخاب کنید."] });
      return;
    }

        if (selectedPaymentMethod === "snapp_pay" && (!snappPayEnabled || snappPayChecking || !snappPayEligible)) {
      setError("در حال حاضر خرید اعتباری با اسنپ‌پی برای این سفارش فعال نیست.");
      return;
    }

    if (!(mode === "saved" && selectedAddress)) {
      const firstName =
        String(form.customer_first_name || "").trim();

      const lastName =
        String(form.customer_last_name || "").trim();

      const nameErrors = {};

      if (!firstName) {
        nameErrors.customer_first_name = [
          "نام را وارد کنید."
        ];
      }

      if (!lastName) {
        nameErrors.customer_last_name = [
          "نام خانوادگی را وارد کنید."
        ];
      }

      if (Object.keys(nameErrors).length > 0) {
        setFormErrors(nameErrors);
        setError(
          "نام و نام خانوادگی گیرنده را کامل وارد کنید."
        );
        return;
      }
    }

    try {
      const checkoutState = await checkCartBeforeSubmit();

      if (!checkoutState) return;

      const { cart: checkedCart, recoveryOrderRef } = checkoutState;

      if (recoveryOrderRef) {
        try {
          setPaymentRedirect({
            ...getPaymentRedirectCopy(selectedPaymentMethod),
            url: "",
            autoRedirect: false
          });

          const payment = selectedPaymentMethod === "snapp_pay"
            ? await requestSnappPayment(recoveryOrderRef).unwrap()
            : await requestPayment(recoveryOrderRef).unwrap();

          const paymentUrl = getPaymentUrl(payment);

          if (!paymentUrl) {
            throw new Error("payment_url_missing");
          }

          const paymentContext = {
            order_ref: recoveryOrderRef,
            attempt_id: payment?.attempt_id || "",
            attempt_uuid: payment?.attempt_uuid || "",
            payable_total: Number(payableTotal || 0),
            items_count: checkedCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
            recovery: true
          };

          trackAnalytics("payment_request_success", {
            metadata: {
              ...paymentContext,
              payment_method: selectedPaymentMethod,
              browser: getPaymentBrowserInfo()
            }
          });

          startPaymentRedirect(paymentUrl, selectedPaymentMethod, paymentContext);
        } catch (paymentErr) {
          setPaymentRedirect(null);

          const message =
            paymentErr?.data?.message ||
            "امکان ادامه پرداخت سفارش قبلی وجود ندارد.";

          setError(message);
          notify(message, "error", "پرداخت انجام نشد");
        }

        return;
      }

      const {
        customer_first_name,
        customer_last_name,
        ...formPayload
      } = form;

      const combinedCustomerName = [
        String(customer_first_name || "").trim(),
        String(customer_last_name || "").trim()
      ]
        .filter(Boolean)
        .join(" ");

      const payload =
        mode === "saved" && selectedAddress
          ? {
              address_id: selectedAddress.id,
              shipping_method_id: selectedShippingMethod?.id,
              discount_code: appliedDiscount?.code || null,
              note: form.note,
              cart: checkedCart
            }
          : {
              ...formPayload,
              customer_name: combinedCustomerName,
              shipping_method_id: selectedShippingMethod?.id,
              discount_code: appliedDiscount?.code || null,
              cart: checkedCart
            };

      const res = await createOrder(payload).unwrap();
      rememberPendingPayment(res.order, checkedCart);

      try {
        setPaymentRedirect({
          ...getPaymentRedirectCopy(selectedPaymentMethod),
          url: "",
          autoRedirect: false
        });

        trackAnalytics("payment_start", {
          metadata: {
            order_ref: orderRef(res.order),
            payable_total: Number(payableTotal || 0),
            payment_method: selectedPaymentMethod,
            items_count: checkedCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
          }
        });

        const payment = selectedPaymentMethod === "snapp_pay"
          ? await requestSnappPayment(orderRef(res.order)).unwrap()
          : await requestPayment(orderRef(res.order)).unwrap();

        const paymentUrl = getPaymentUrl(payment);

        if (paymentUrl) {
          const paymentContext = {
            order_ref: orderRef(res.order),
            order_number: res.order?.order_number || "",
            attempt_id: payment?.attempt_id || "",
            attempt_uuid: payment?.attempt_uuid || "",
            payable_total: Number(payableTotal || 0),
            items_count: checkedCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
          };

          trackAnalytics("payment_request_success", {
            metadata: {
              ...paymentContext,
              payment_method: selectedPaymentMethod,
              browser: getPaymentBrowserInfo()
            }
          });

          startPaymentRedirect(paymentUrl, selectedPaymentMethod, paymentContext);
          return;
        }

        setPaymentRedirect(null);
        throw new Error("payment_url_missing");
      } catch (paymentErr) {
        trackAnalytics("payment_failed", {
          metadata: {
            order_ref: orderRef(res.order),
            stage: selectedPaymentMethod === "snapp_pay" ? "request_snapp_payment" : "request_payment",
            message: paymentErr?.data?.message || paymentErr?.message || "payment request failed"
          }
        });

        setPaymentRedirect(null);
        navigate(orderSuccessUrl(res.order), {
          state: { order: res.order, keepCart: true }
        });
        setTimeout(() => {
          notify(paymentErr?.data?.message || "سفارش ثبت شد؛ اتصال به صفحه پرداخت انجام نشد.", "warning", "پرداخت انجام نشد");
        }, 300);
        return;
      }

    } catch (err) {
      const errors = getApiErrors(err);

      trackAnalytics("checkout_submit_failed", {
        metadata: {
          stage: "submit_order",
          message: getErrorMessage(err),
          fields: Object.keys(errors || {}),
          browser: getPaymentBrowserInfo()
        }
      });

      setFormErrors(errors);
      setError(Object.keys(errors).length ? "موارد مشخص‌شده را اصلاح کنید." : getErrorMessage(err));
      notify(getErrorMessage(err), "error", "ثبت سفارش ناموفق بود");
    }
  };

  const submitting = response.isLoading || paymentResponse.isLoading || snappPaymentResponse.isLoading || validateCartResponse.isLoading;

  return (
    <>
      <Nav />

      {isRedirectingPayment && (
        <div className="payment-redirect-overlay" dir="rtl">
          <div className="payment-redirect-card">
            <span className="payment-redirect-spinner"></span>
            <strong>{paymentRedirect?.title || "انتقال به پرداخت"}</strong>
            <p>{paymentRedirect?.message || "در حال آماده‌سازی صفحه پرداخت..."}</p>
            {paymentRedirect?.url && (
              <div className="payment-redirect-actions">
                <button
                  type="button"
                  className="payment-redirect-button"
                  onClick={() => {
                    trackPaymentTelemetry("payment_manual_open_clicked", {
                      metadata: {
                        ...(paymentRedirect?.context || {}),
                        trigger: "checkout_overlay"
                      }
                    });
                    goToTrackedPaymentUrl(paymentRedirect.url, {
                      ...(paymentRedirect?.context || {}),
                      trigger: "manual"
                    });
                  }}
                >
                  ادامه پرداخت
                </button>
                {(paymentRedirect?.embedded || !paymentRedirect?.autoRedirect) && (
                  <button type="button" className="payment-redirect-link-button" onClick={copyCurrentPaymentUrl}>
                    کپی لینک پرداخت
                  </button>
                )}
              </div>
            )}
            {copyPaymentMessage && <small>{copyPaymentMessage}</small>}
            {paymentRedirect?.help && <small>{paymentRedirect.help}</small>}
          </div>
        </div>
      )}

      <main className="checkout-page">
        <div className="site-container">
          <div className="checkout-head">
            <span>تکمیل خرید</span>
            <h1>تکمیل خرید</h1>
          </div>

          {!userToken && (
            <div className="checkout-login-alert">
              <span>برای ادامه وارد شوید.</span>
              <Link to="/login" state={{ from: "/checkout" }} onClick={() => sessionStorage.setItem("redirectAfterLogin", "/checkout")}>
                ورود سریع
              </Link>
            </div>
          )}

          {error && <div className="checkout-error">{error}</div>}

          {error && (
            <SupportHint
              className="checkout-support-hint"
              tone="warning"
              text="اگر برای ثبت سفارش یا پرداخت نیاز به راهنمایی دارید، با ما در ارتباط باشید."
            />
          )}

          {shouldShowPaymentBrowserHint && (
            <div className="checkout-browser-hint">
              <strong>راهنمای پرداخت</strong>
              <span>اگر صفحه پرداخت باز نشد، سایت را با مرورگر گوشی باز کنید.</span>
            </div>
          )}

          {!cart.length ? (
            <div className="checkout-empty">
              <h2>سبد خرید شما خالی است</h2>
              <p>سبد خرید خالی است.</p>
              <Link to="/">بازگشت به فروشگاه</Link>
            </div>
          ) : (
            <form onSubmit={submitOrder} className="checkout-grid">
              <section className="checkout-card">
                <div className="checkout-card-title">
                  <FiMapPin size={21} />
                  <div>
                    <h2>آدرس</h2>
                  </div>
                </div>

                {userToken && addressesLoading && <div className="checkout-address-loading">دریافت آدرس‌ها...</div>}

                {userToken && addresses.length > 0 && (
                  <div className="checkout-address-mode">
                    <button type="button" className={mode === "saved" ? "active" : ""} onClick={() => setMode("saved")}>
                      آدرس‌ها
                    </button>
                    <button type="button" className={mode === "new" ? "active" : ""} onClick={() => setMode("new")}>
                      جدید
                    </button>
                  </div>
                )}

                {userToken && mode === "saved" && addresses.length > 0 ? (
                  <div className="checkout-address-list">
                    {addresses.map((address) => (
                      <label key={address.id} className={`checkout-address-option ${String(selectedAddressId) === String(address.id) ? "active" : ""}`}>
                        <input type="radio" name="selected_address" value={address.id} checked={String(selectedAddressId) === String(address.id)} onChange={(e) => setSelectedAddressId(e.target.value)} />

                        <div>
                          <div className="checkout-address-option-head">
                            <strong>{address.receiver_name}</strong>
                            {address.is_default && <span>پیش‌فرض</span>}
                          </div>

                          <p>
                            {[address.province, address.city, address.address, address.plaque ? `پلاک ${address.plaque}` : "", address.unit ? `واحد ${address.unit}` : ""]
                              .filter(Boolean)
                              .join("، ")}
                          </p>

                          <small>{address.receiver_mobile}</small>

                          <button type="button" onClick={() => fillFromAddress(address)}>
                            <FiEdit3 size={15} />
                            ویرایش
                          </button>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="checkout-fields-grid">
                      <div className={`checkout-field ${getFieldError(formErrors, "customer_first_name") ? "has-field-error" : ""}`}>
                      <label>
                        <FiUser size={16} />
                        نام *
                      </label>

                      <input
                        name="customer_first_name"
                        value={form.customer_first_name}
                        onChange={onChange}
                        placeholder="نام"
                        autoComplete="given-name"
                        required
                      />

                      <FieldError
                        message={getFieldError(
                          formErrors,
                          "customer_first_name"
                        )}
                      />
                    </div>

                    <div className={`checkout-field ${getFieldError(formErrors, "customer_last_name") ? "has-field-error" : ""}`}>
                      <label>
                        <FiUser size={16} />
                        نام خانوادگی *
                      </label>

                      <input
                        name="customer_last_name"
                        value={form.customer_last_name}
                        onChange={onChange}
                        placeholder="نام خانوادگی"
                        autoComplete="family-name"
                        required
                      />

                      <FieldError
                        message={getFieldError(
                          formErrors,
                          "customer_last_name"
                        )}
                      />
                    </div>

                    <div className={`checkout-field ${getFieldError(formErrors, "customer_mobile") ? "has-field-error" : ""}`}>
                        <label>
                          <FiPhone size={16} />
                          موبایل
                        </label>
                        <input name="customer_mobile" value={form.customer_mobile} onChange={onChange} placeholder="۰۹..." className="ltr" />
                        <FieldError message={getFieldError(formErrors, "customer_mobile")} />
                      </div>
                    </div>

                    <div className="checkout-fields-grid">
                      <div className={`checkout-field ${getFieldError(formErrors, "province") ? "has-field-error" : ""}`}>
                        <label>
                          <FiMapPin size={16} />
                          استان
                        </label>
                        <input name="province" value={form.province} onChange={onChange} placeholder="تهران" />
                        <FieldError message={getFieldError(formErrors, "province")} />
                      </div>

                      <div className={`checkout-field ${getFieldError(formErrors, "city") ? "has-field-error" : ""}`}>
                        <label>
                          <FiMapPin size={16} />
                          شهر
                        </label>
                        <input name="city" value={form.city} onChange={onChange} placeholder="تهران" />
                        <FieldError message={getFieldError(formErrors, "city")} />
                      </div>
                    </div>

                    <div className={`checkout-field ${getFieldError(formErrors, "address") ? "has-field-error" : ""}`}>
                      <label>
                        <FiMapPin size={16} />
                        آدرس
                      </label>
                      <textarea name="address" value={form.address} onChange={onChange} placeholder="خیابان، کوچه، ساختمان" rows="4" />
                      <FieldError message={getFieldError(formErrors, "address")} />
                    </div>

                    <div className="checkout-fields-grid checkout-fields-grid-3">
                      <div className={`checkout-field ${getFieldError(formErrors, "plaque") ? "has-field-error" : ""}`}>
                        <label>پلاک</label>
                        <input name="plaque" value={form.plaque} onChange={onChange} placeholder="پلاک" />
                        <FieldError message={getFieldError(formErrors, "plaque")} />
                      </div>

                      <div className={`checkout-field ${getFieldError(formErrors, "unit") ? "has-field-error" : ""}`}>
                        <label>واحد</label>
                        <input name="unit" value={form.unit} onChange={onChange} placeholder="واحد" />
                        <FieldError message={getFieldError(formErrors, "unit")} />
                      </div>

                      <div className={`checkout-field ${getFieldError(formErrors, "postal_code") ? "has-field-error" : ""}`}>
                        <label>کد پستی</label>
                        <input name="postal_code" value={form.postal_code} onChange={onChange} placeholder="کد پستی" className="ltr" />
                        <FieldError message={getFieldError(formErrors, "postal_code")} />
                      </div>
                    </div>

                    <label className="checkout-save-address">
                      <input type="checkbox" name="save_address" checked={form.save_address} onChange={onChange} />
                      <span>ذخیره آدرس</span>
                    </label>
                  </>
                )}

                <div className="checkout-field">
                  <label>
                    <FiFileText size={16} />
                    یادداشت
                  </label>
                  <textarea name="note" value={form.note} onChange={onChange} placeholder="اختیاری" rows="3" />
                </div>
              </section>

              <section className="checkout-card checkout-step-card checkout-shipping-section">
                <div className="checkout-card-title">
                  <FiTruck size={21} />
                  <div>
                    <h2>ارسال</h2>
                  </div>
                </div>

                <div className="checkout-clean-method-options">
                  {shippingLoading && <div className="checkout-shipping-loading">دریافت ارسال...</div>}

                  {!shippingLoading && shippingMethods.length === 0 && (
                    <div className="checkout-shipping-empty">روش ارسال فعال نیست.</div>
                  )}

                  {shippingMethods.map((method) => (
                    <label key={method.id} className={`checkout-clean-shipping-card ${String(selectedShippingMethodId) === String(method.id) ? "is-active" : ""}`}>
                      <input
                        className="checkout-clean-method-radio"
                        type="radio"
                        name="shipping_method"
                        value={method.id}
                        checked={String(selectedShippingMethodId) === String(method.id)}
                        onChange={(e) => setSelectedShippingMethodId(e.target.value)}
                      />

                      <span className="checkout-clean-method-icon">
                        <FiTruck size={18} />
                      </span>

                      <div className="checkout-clean-method-body">
                        <strong>{method.title}</strong>
                      </div>

                      <b className="checkout-clean-shipping-price">
                        {method.type === "fixed" ? <Money value={method.cost} /> : method.type === "free" ? "بدون هزینه ارسال" : "پس‌کرایه"}
                      </b>
                    </label>
                  ))}
                </div>
              </section>

              <section className="checkout-card checkout-step-card checkout-payment-section">
                <div className="checkout-card-title">
                  <FiCreditCard size={21} />
                  <div>
                    <h2>پرداخت</h2>
                  </div>
                </div>

                <div className="checkout-payment-options checkout-payment-clean-options">
                  <label className={`checkout-payment-choice ${selectedPaymentMethod === "online_gateway" ? "is-active" : ""}`}>
                    <input
                      className="checkout-payment-choice-radio"
                      type="radio"
                      name="payment_method"
                      value="online_gateway"
                      checked={selectedPaymentMethod === "online_gateway"}
                      onChange={() => setSelectedPaymentMethod("online_gateway")}
                    />

                    <span className="checkout-payment-choice-icon checkout-payment-choice-icon-gateway">
                      <FiCreditCard size={19} />
                    </span>

                    <div className="checkout-payment-choice-body">
                      <strong>پرداخت آنلاین</strong>
                      <small>پرداخت امن از طریق درگاه بانکی</small>
                    </div>
                  </label>

                  {shouldShowSnappPay && (
                    <label className={`checkout-payment-choice checkout-payment-choice-snapp ${selectedPaymentMethod === "snapp_pay" ? "is-active" : ""} ${!snappPaySelectable ? "is-disabled" : ""}`}>
                      <input
                        className="checkout-payment-choice-radio"
                        type="radio"
                        name="payment_method"
                        value="snapp_pay"
                        checked={selectedPaymentMethod === "snapp_pay"}
                        onChange={() => snappPaySelectable && setSelectedPaymentMethod("snapp_pay")}
                        disabled={!snappPaySelectable}
                      />

                      <span className="checkout-payment-choice-icon checkout-payment-choice-icon-snapp">
                        <img src={snappPayLogo} alt="SnappPay" />
                      </span>

                      <div className="checkout-payment-choice-body">
                        <strong>{snappPayTitle}</strong>
                        <small>{snappPayDescription}</small>
                      </div>
                    </label>
                  )}
                </div>
              </section>

              <aside className="checkout-summary">
                <h2>سفارش</h2>

                <details className="checkout-items-drawer">
                  <summary>
                    <span>کالاها</span>
                    <b>{toPersianNumber(cart.length)} مورد</b>
                  </summary>

                  <div className="checkout-items">
                    {cart.map((item) => {
                      const basePrice = Number(item.price || 0);
                      const finalPrice = getFinalPrice(item);
                      const hasDiscount = basePrice > 0 && finalPrice > 0 && finalPrice < basePrice;

                      return (
                        <div className="checkout-item" key={`${item._id}-${item.color || "no-color"}-${item.size || "no-size"}`}>
                          <img src={imageUrl(item.image1 || item.main_image)} alt={item.title || item.name || ""} />

                          <div>
                            <strong>{item.title || item.name}</strong>
                            <span>
                              {toPersianNumber(item.quantity)} عدد
                              {item.color ? ` | رنگ: ${item.color}` : ""}
                              {item.size ? ` | سایز: ${toPersianNumber(item.size)}` : ""}
                            </span>
                          </div>

                          <b className={hasDiscount ? "checkout-positive-price" : ""}>{<Money value={finalPrice * item.quantity} />}</b>
                        </div>
                      );
                    })}
                  </div>
                </details>

                <div className="checkout-discount-box">
                  <h3>تخفیف</h3>


                  <div className="checkout-discount-row">
                    <input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="مثلاً JOOTI20"
                    />

                    <button type="button" onClick={applyCode} disabled={discountResponse.isLoading || !selectedShippingMethod}>
                      {discountResponse.isLoading ? "بررسی..." : "اعمال"}
                    </button>
                  </div>

                  {discountError && <div className="checkout-discount-error">{discountError}</div>}

                  {appliedDiscount && (
                    <div className="checkout-discount-success">
                      کد {appliedDiscount.code} اعمال شد.
                    </div>
                  )}
                </div>

                <div className="checkout-price-lines">
                  <div>
                    <span>کالاها</span>
                    <strong className="checkout-money"><Money value={total} /></strong>
                  </div>
                  <div>
                    <span>ارسال</span>
                    <strong className="checkout-money"><bdi>{selectedShippingMethod ? selectedShippingMethod.type === "fixed" ? <Money value={shippingCost} /> : selectedShippingMethod.type === "free" ? "بدون هزینه ارسال" : "پس‌کرایه" : "انتخاب نشده"}</bdi></strong>
                  </div>
                  {discountAmount > 0 && (
                    <div>
                      <span>تخفیف کالاها</span>
                      <strong className="checkout-money checkout-negative-price"><Money value={discountAmount} negative /></strong>
                    </div>
                  )}
                  {shippingDiscountAmount > 0 && (
                    <div>
                      <span>تخفیف ارسال</span>
                      <strong className="checkout-money checkout-negative-price"><Money value={shippingDiscountAmount} negative /></strong>
                    </div>
                  )}
                </div>

                <div className="checkout-total">
                  <span>قابل پرداخت</span>
                  <strong className="checkout-money checkout-total-money"><Money value={payableTotal} /></strong>
                </div>

                <button type="submit" disabled={submitting || !userToken || !selectedShippingMethod}>
                  {submitting ? "در حال اتصال..." : selectedPaymentMethod === "snapp_pay" ? "ادامه با اسنپ‌پی" : "پرداخت و ثبت سفارش"}
                </button>

                {!userToken && (
                  <Link to="/login" state={{ from: "/checkout" }} className="checkout-login-submit" onClick={() => sessionStorage.setItem("redirectAfterLogin", "/checkout")}>
                    <FiPlus size={17} />
                    ورود و ادامه خرید
                  </Link>
                )}

                <Link to="/cart" className="checkout-back-link">
                  بازگشت به سبد
                </Link>
              </aside>
            </form>
          )}
        </div>
      </main>
    </>
  );
};

export default Checkout;
